import qs from 'qs'
import fetchContentType from 'src/lib/Strapi/fetchContentType'
import spreadStrapiData from 'src/lib/Strapi/spreadStrapiData'

/**
 * Fetches integration cards data, processing each card to ensure proper link text.
 *
 * @param {Object} options - Options for fetching integration cards.
 * @param {string} options.buttonText - Default button text for the card links.
 * @param {number} [options.page=1] - Current page number for pagination.
 * @param {number} [options.limit=6] - Number of items per page.
 * @param {Array<number>} [options.integrationTagIdsFilter] - Tag IDs to filter by (uses $in operator)
 *  Useful for filtering cards by tags on the integration detail page
 * @param {Array<number>} [options.excludeIds] - Integration IDs to exclude from results (uses $notIn operator)
 * @return {Promise<Object>} An object containing the count of cards, category ID, and integrations data.
 */
export async function fetchIntegrationsCardCategory({
  buttonText,
  page = 1,
  limit = 6,
  integrationTagIdsFilter = [],
  excludeIds = [],
}) {
  try {
    const query = {
      pagination: {
        pageSize: limit,
        page: page,
      },
      filters: {},
    }

    // Add filters for integration tags
    if (
      Array.isArray(integrationTagIdsFilter) &&
      integrationTagIdsFilter?.length > 0
    ) {
      query.filters.integration_tags = {
        id: { $in: integrationTagIdsFilter },
      }
    }

    // Exclude specific integration(s)
    if (Array.isArray(excludeIds) && excludeIds.length > 0) {
      query.filters.id = { $notIn: excludeIds }
    }

    const queryString = qs.stringify(query, { encodeValuesOnly: true })
    const data = await fetchContentType('integrations', queryString, false)

    const meta = data.meta
    const spreadIntegrations = spreadStrapiData(data)

    // Process each card to format the link object
    const integrations = spreadIntegrations.map((card) => {
      if (!card.isExternal && card.slug) {
        return {
          ...card,
          link: {
            href: `/integrations/${card.slug}`,
            text: card.link?.text || buttonText,
          },
        }
      }

      return {
        ...card,
        link: card.link
          ? { ...card.link, text: card.link.text || buttonText }
          : { text: buttonText },
      }
    })

    // Extract the total number of cards from the response metadata
    const cardsCount = meta.pagination.total

    return {
      count: cardsCount,
      categoryId: null, // Placeholder for category ID if applicable
      integrations,
    }
  } catch (error) {
    // Log any errors that occur during fetching
    console.error('FetchIntegrationsCardCategoryError', error)
  }
}
